<script>
import { wizardListenerMixin } from '@/mixins/wizard';
import Paper from '@/components/layout/WizardOverviewPaper';
import EzButton from '@/components/ui/Button/EzButton';
import VEntityInfo from '@/components/v3/elements/VEntityInfo/index';
import { mapActions, mapMutations, mapState } from 'vuex';
import { ONBOARDING_STEP_2, ONBOARDING_STEP_3 } from '@/views/public/onboarding/new-outlet/steps';
import flash from '@/components/ui/FlashMessage';
import Tos from '@/views/public/onboarding/Tos';
import httpService from '@/api/http';
import EzCheckbox from '@/components/ui/Checkbox/Checkbox';

/**
 *
 * @version 1.0.0
 * @since 2.4.0
 */
export default {
  mixins: [wizardListenerMixin],
  components: {
    Paper,
    EzButton,
    VEntityInfo,
    Tos,
    EzCheckbox,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    quoteId: {
      type: String,
      required: false,
      default: null,
    },
    fromShoppingCart: {
      type: Boolean,
      required: false,
      default: false,
    },
    distributor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      buttonType: 'primary',
      termsAlert: false,
      termsAccepted: false,
    };
  },
  computed: {
    ...mapState('entities/venues', ['publicOnboarding']),
    config() {
      return {
        headers: {
          'X-Public-Token': this.token,
        },
      };
    },
    venue() {
      return this.publicOnboarding.venue;
    },
    shippingAddress() {
      return this.venue.shippingAddress;
    },
    billingAddress() {
      return this.venue.billingAddress;
    },
    hasOrderingUser() {
      return Object.keys(this.publicOnboarding.orderingUser).length;
    },
    isTermsAccepted() {
      return this.publicOnboarding.termsAndConditions;
    },
  },
  watch: {
    isTermsAccepted(val) {
      if (val) {
        this.termsAccepted = val;
        this.buttonType = 'green';
        this.enableNextStep();
      } else {
        this.termsAccepted = val;
        this.disableNextStep();
      }
    },
  },
  methods: {
    addressFormat(data) {
      return [
        ...(data.street ? [`${data.street}`] : []),
        ...(data.city ? [`${data.zipCode} ${data.city}`] : []),
        ...(data.country ? [`${data.country}`] : []),
        ...(data.state ? [`${data.state}`] : []),
      ].join(', ');
    },
    editContacts() {
      this.$emit('stepBack', ONBOARDING_STEP_3);
    },
    editVenue() {
      this.$emit('stepBack', ONBOARDING_STEP_2);
    },
    readTermsAndConditions() {
      this.$refs.tosModal.open();
    },
    async onNextStep() {
      if (!this.termsAccepted) {
        this.termsAlert = true;
        flash.error({ title: 'Please accept the Terms and Conditions' });
        return;
      }
      try {
        const { data: connectData } = await this.connectPublicExistingOnboarding({
          distributorId: this.distributor.id,
          venueId: this.venue.id,
          config: this.config,
          data: {
            termsAndConditions: this.termsAccepted,
            ...(this.quoteId ? { quoteId: +this.quoteId } : {}),
          },
        });
        const { data } = await httpService.put(
          `public/b2b-portal/login${this.fromShoppingCart ? '?page=shopping_cart' : ''}`,
          {
            email: this.publicOnboarding.owner.email,
            ...(this.publicOnboarding.venue?.id ? { venueId: this.publicOnboarding.venue.id } : {}),
          },
          this.config,
        );
        if (data.data?.url) {
          window.location.href = `${data.data.url}`;
        } else {
          flash.success({
            title: this.$t('venues.onboarding.flash.title'),
            message: this.$t('venues.onboarding.flash.message', {
              distributorName: this.distributor.name,
            }),
          });
          this.$router.push({
            name: 'onboarding-success',
            params: {
              token: this.token,
              onboarding: this.publicOnboarding,
              fromShoppingCart: this.fromShoppingCart,
              isInvitation: connectData?.data?.status === 'invitation' || false,
            },
          });
        }
        this.CLEAR_PUBLIC_ONBOARDING();
      } catch (e) {
        await this.$router.push({ name: 'platform-login' });
        flash.error({ title: 'Something went wrong' });
      }
    },
    ...mapActions('entities/venues', ['connectPublicExistingOnboarding']),
    ...mapMutations('entities/venues', ['CLEAR_PUBLIC_ONBOARDING', 'UPDATE_PUBLIC_ONBOARDING']),
  },
  mounted() {
    this.disableFooterActions();
    this.enableNextStep();
    this.termsAccepted = this.isTermsAccepted;
  },
};
</script>
<template>
  <div>
    <div class="terms-btn-container mb-12">
      <ez-button @click="readTermsAndConditions" :type="buttonType">
        <template v-if="termsAccepted">
          <font-awesome-icon icon="check" />
        </template>
        Read Terms & Conditions
      </ez-button>
    </div>
    <div
      :class="{
        'terms-btn-container': true,
        'terms-btn-container--alert': termsAlert,
        'mb-24': true,
      }"
    >
      <ez-checkbox
        label="I accept the Terms & Conditions"
        class="cursor-pointer mt-12"
        :checked="termsAccepted"
        @change="() => (termsAccepted = !termsAccepted)"
      />
    </div>
    <paper>
      <div class="venue">
        <VEntityInfo
          class="mb-16"
          imageSize="64px"
          :imageUrl="venue.logo"
          imageBorderRadius="50%"
          imageHasBorder
        />
        <p class="venue__name">{{ venue.name }}</p>
      </div>
    </paper>
    <paper>
      <template #title>Main Info</template>
      <template #actions>
        <ez-button @click="editVenue" type="link" formType="button">
          <font-awesome-icon icon="pen" />
          <span>Edit</span>
        </ez-button>
      </template>
      <div class="info-segment">
        <p class="info-segment__title">{{ $t('global.venue') }} Name</p>
        <p class="info-segment__info">{{ venue.name }}</p>
      </div>
      <div v-if="venue.customInfo" class="info-segment mt-16">
        <p class="info-segment__title">Additional Info</p>
        <p class="info-segment__info">{{ venue.customInfo }}</p>
      </div>
      <hr />
      <div class="info-segment mt-16">
        <p class="info-segment__title">ADDRESSES</p>
      </div>
      <div class="info-segment mt-16">
        <p class="info-segment__title">Delivery Address</p>
        <p class="info-segment__info">{{ addressFormat(shippingAddress) }}</p>
      </div>
      <div class="info-segment mt-16" v-if="billingAddress">
        <p class="info-segment__title">Billing Address</p>
        <p class="info-segment__info">{{ addressFormat(billingAddress) }}</p>
      </div>
    </paper>
    <paper>
      <template #title>Account</template>
      <div class="contact-item">
        <div class="contact-item__name">{{ publicOnboarding.owner.name }}</div>
        <div class="contact-item__type">{{ $t('global.accountRep') }}</div>
      </div>
    </paper>
    <paper>
      <template #title>Contacts</template>
      <template #actions>
        <ez-button @click="editContacts" type="link" formType="button">
          <font-awesome-icon icon="pen" />
          <span>Edit</span>
        </ez-button>
      </template>
      <div class="contact-item">
        <div class="contact-item__name">{{ publicOnboarding.financeUser.name }}</div>
        <div class="contact-item__type">Finance Contact</div>
      </div>
      <div class="contact-item" v-if="hasOrderingUser">
        <div class="contact-item__name">{{ publicOnboarding.orderingUser.name }}</div>
        <div class="contact-item__type">Ordering Contact</div>
      </div>
    </paper>
    <tos ref="tosModal" :distributor="distributor" :token="token"></tos>
  </div>
</template>
<style lang="scss" scoped>
.venue {
  display: flex;
  flex-direction: column;
  align-items: center;
  &__name {
    margin: 0;
    @include font-size(16px, 19px);
    font-weight: 500;
    color: $color-gray-25;
  }
}
.info-segment {
  &__title {
    @include font-size(12px, 14px);
    color: $color-gray-6C;
    margin: 0 0 6px 0;
    font-weight: 500;
  }
  &__info {
    @include font-size(14px, 16px);
    color: #2b2d3e;
    margin: 0;
  }
}
.contact-item {
  @extend %flex-center;
  justify-content: space-between;
  @include font-size(14px, 16px);
  padding: 10px 0;
  &__name {
    color: $color-gray-25;
  }
  &__type {
    color: $color-gray-6C;
  }
}

.accepted-tos {
  @extend %flex-center;
  &__icon {
    color: $color-primary-green;
  }
  &__text {
    color: $color-gray-6C;
    @include font-size(14px, 16px);
    margin-left: 8px;
  }
}
.terms-btn-container {
  display: flex;
  justify-content: center;
  align-items: center;

  &--alert {
    :deep() .input-group__label {
      color: $color-primary-red;
    }
  }
}
</style>
